import React, { useState } from 'react';
import { Col, Row, Tooltip } from 'antd';
import { KeyOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import IntlMessage from 'components/ui/IntlMessage';
import dayjs from 'dayjs';
import Highlighter from 'react-highlight-words';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';

const setLocale = (isLocaleOn, localeKey) =>
  isLocaleOn ? <IntlMessage id={localeKey} /> : localeKey?.toString?.() || '';

const RoundedAvatar = ({
  record,
  size = 'large',
  showEmail = false,
  showName = false,
  showAge = false,
  showTooltip = false,
  showIcon = false,
  cursorPointer = false,
  showCpfCnpj = false,
  upperCase = false,
  className,
  highlighterText = null,
  shape = 'circle',
  profile = null,
  title = null, // pode vir como ReactNode
  subtitle = null,
  hideLoading = false,
}) => {
  // campos do usuário
  const foreColor = record?.foreColor || record?.foreColor || record?.__foreColor;
  const backColor = record?.backColor || record?.backColor || record?.__backColor;

  // Avatar source priority: avatar.base64Thumb (fast for small avatars) > avatar.thumbUrl > legacy pictureUrl
  const avatar = record?.avatar;
  const avatarSrc = avatar?.base64Thumb || avatar?.thumbUrl || avatar?.storageUrl;
  const rawPictureUrl =
    avatarSrc || record?.pictureUrl || record?.pictureUrl || record?.__pictureUrl;
  const pictureUrl = rawPictureUrl !== 'n/a' ? rawPictureUrl : null;
  const name =
    typeof title === 'string' && title.trim() ? title : record?.name || record?.fullName || ''; // string garantida

  const cpf = record?.cpf || record?.cpf || record?.__cpf;
  const cnpj = record?.cnpj || record?.cnpj || record?.__cnpj;
  const uniqueKey = cpf || cnpj;
  const email = record?.email?.value || record?.email || '';
  const birthdate = record?.birthdate || record?.birthdate || record?.__birthdate;
  const age = birthdate && dayjs().diff(birthdate, 'years');

  const [highlighter] = useState(highlighterText);

  // força re-render do avatar se nome/src mudarem
  const avatarKey = `${pictureUrl || 'no-src'}::${name}`;

  return (
    <Row align="middle" className={`${className || ''}`} wrap={false}>
      <Col>
        <Tooltip title={name} placement="top" open={showTooltip}>
          {showIcon && profile === 'owner' ? (
            <Tooltip title={setLocale(true, 'owner')}>
              <SafetyCertificateOutlined className="rounded-avatar-profile-icon" />
            </Tooltip>
          ) : null}

          {showIcon && profile === 'keyUser' ? (
            <Tooltip title={setLocale(true, 'key_user_title')}>
              <KeyOutlined className="rounded-avatar-profile-icon" />
            </Tooltip>
          ) : null}

          {showIcon && profile === 'guest' ? (
            <Tooltip title={setLocale(true, 'common_user_title')}>
              <LockOutlined twoToneColor="red" className="rounded-avatar-profile-icon" />
            </Tooltip>
          ) : null}

          <SimpleAvatar
            key={avatarKey} // garante atualização visual quando iniciais/src mudarem
            size={size === 'large' ? 40 : size === 'small' ? 24 : size}
            shape={shape}
            src={pictureUrl}
            name={name}
            backColor={backColor}
            foreColor={foreColor}
            hideLoading={hideLoading}
            style={{
              cursor: cursorPointer ? 'pointer' : 'default',
            }}
          />
        </Tooltip>
      </Col>

      <Col>
        {(showName || showEmail) && (
          <Col>
            {showName && (
              <div className={`ml-2 font-weight-bold ${upperCase ? 'text-uppercase' : ''}`}>
                {highlighter ? (
                  <Highlighter
                    highlightStyle={{ backgroundColor: '#fedf17', padding: 0 }}
                    searchWords={highlighter}
                    autoEscape
                    textToHighlight={name ? name.toString() : ''}
                  />
                ) : (
                  name
                )}
                {birthdate && showAge && <span>, {age}</span>}
              </div>
            )}

            {!showCpfCnpj && showEmail && (
              <span className="ml-2 text-muted">
                {highlighter ? (
                  <Highlighter
                    highlightStyle={{ backgroundColor: '#fedf17', padding: 0 }}
                    searchWords={highlighter}
                    autoEscape
                    textToHighlight={email ? email.toString() : ''}
                  />
                ) : (
                  email
                )}
              </span>
            )}

            {showCpfCnpj && (
              <span className="ml-2 text-muted">
                {highlighter ? (
                  <Highlighter
                    highlightStyle={{ backgroundColor: '#fedf17', padding: 0 }}
                    searchWords={highlighter}
                    autoEscape
                    textToHighlight={uniqueKey ? uniqueKey.toString() : ''}
                  />
                ) : (
                  uniqueKey
                )}
              </span>
            )}
          </Col>
        )}
      </Col>
    </Row>
  );
};

export default RoundedAvatar;
